import { useState, useEffect } from "react";
import { Check, Crown, Users } from "lucide-react";
import { Reveal } from "./Reveal";
import { Countdown } from "./Countdown";
import { EARLY_BIRD_DATE, api } from "@/lib/api";
import { PaymentDialog } from "./PaymentDialog";

const INCLUDED = [
  "6 weeks of live, hands-on instruction",
  "Crowned™ Pro Level 1 certification",
  "Complete starter tool & product kit",
  "Signature technique masterclasses",
  "Business & pricing playbook",
  "Portfolio photo day",
  "Lifetime alumni community access",
  "1:1 mentorship & feedback sessions",
];

const dateEarlyBird = Date.now() <= EARLY_BIRD_DATE.getTime();

export const Pricing = () => {
  const [dialog, setDialog] = useState({ open: false, pkg: "deposit" });
  const [seats, setSeats] = useState(null);
  const openPay = (pkg) => setDialog({ open: true, pkg });

  useEffect(() => {
    let active = true;
    const load = () => api.get("/seats").then((r) => active && setSeats(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 30000);
    return () => {
      active = false;
      clearInterval(i);
    };
  }, []);

  // Backend is source of truth; fall back to date-based estimate before load.
  const isEarlyBird = seats ? seats.early_bird_active : dateEarlyBird;
  const FULL_PRICE = seats ? `$${Math.round(seats.full_price)}` : dateEarlyBird ? "$449" : "$549";

  return (
  <section id="pricing" data-testid="pricing-section" className="bg-wine py-24 sm:py-32 relative grain overflow-hidden">
    <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-gold/10 blur-[130px]" />
    <div className="relative z-10 max-w-[1100px] mx-auto px-6">
      <Reveal className="text-center max-w-2xl mx-auto mb-16">
        <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
          Enrollment
        </span>
        <h2 className="font-display font-light text-cream text-5xl sm:text-6xl leading-[1] mt-5">
          Claim your seat in the <span className="italic text-gold">first cohort.</span>
        </h2>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="grid lg:grid-cols-2 rounded-[2rem] overflow-hidden border border-gold/25 bg-wine-deep/60 backdrop-blur">
          {/* Left: price */}
          <div className="p-10 sm:p-12 border-b lg:border-b-0 lg:border-r border-gold/20 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 text-gold mb-6">
                <Crown className="h-5 w-5" />
                <span className="font-mono text-[11px] uppercase tracking-[0.2em]">Crowned™ Pro · Level 1</span>
              </div>
              <div className="flex items-end gap-3">
                <span className="font-display text-7xl text-cream leading-none">{FULL_PRICE}</span>
                {isEarlyBird && (
                  <span className="font-display text-3xl text-cream/40 line-through mb-1">$549</span>
                )}
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-gold mt-4">
                {isEarlyBird ? "Early-bird · first 5 students only" : "Full tuition"}
              </p>
              <p className="text-cream/60 mt-4 leading-relaxed">
                Reserve now with a <span className="text-cream font-semibold">$150 deposit</span> or pay
                in full. Only 5 seats in this founding cohort — early-bird pricing is limited to the
                first 5 students.
              </p>
            </div>
            <div className="mt-10 space-y-3">
              {seats && (
                <div data-testid="seats-remaining" className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-gold">
                      <Users className="h-3.5 w-3.5" />
                      {seats.remaining} of {seats.total} seats remaining
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-cream/40">
                      {seats.taken} reserved
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-cream/10 overflow-hidden">
                    <div
                      className="h-full bg-gold transition-[width] duration-700"
                      style={{ width: `${Math.min(100, (seats.taken / seats.total) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
              <button
                data-testid="pay-deposit-btn"
                onClick={() => openPay("deposit")}
                className="w-full rounded-full bg-gold px-8 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-wine-deep hover:bg-cream transition-colors duration-300"
              >
                Secure My Seat · $150 Deposit
              </button>
              <button
                data-testid="pay-full-btn"
                onClick={() => openPay("full")}
                className="w-full rounded-full border border-cream/25 px-8 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-cream hover:border-gold hover:text-gold transition-colors duration-300"
              >
                Pay In Full · {FULL_PRICE}
              </button>
              <div className="pt-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/40 mb-3">
                  Early-bird pricing ends in
                </div>
                <Countdown target={EARLY_BIRD_DATE} />
              </div>
            </div>
          </div>

          {/* Right: whats included */}
          <div className="p-10 sm:p-12 bg-cream/[0.03]">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-cream/50 mb-6">
              Everything included
            </div>
            <ul className="space-y-4">
              {INCLUDED.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold">
                    <Check className="h-3 w-3 text-wine-deep" strokeWidth={3} />
                  </span>
                  <span className="text-cream/85">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
    </div>

    <PaymentDialog
      open={dialog.open}
      onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
      pkg={dialog.pkg}
      fullPrice={FULL_PRICE}
    />
  </section>
  );
};
